import { useEffect, useMemo, useState } from "react";
import imageCompression from "browser-image-compression";
import { getSupabase } from "../lib/supabase";
import { uploadCaseStudyImage } from "../utils/uploadCaseStudyImage";
import { SERVICE_OPTIONS, INDUSTRY_OPTIONS } from "../config/constants";

const COUNTRY_OPTIONS = ["Global", "Pakistan", "UAE", "UK", "USA"];

const EMPTY_FORM = {
  title: "",
  slug: "",
  industry: INDUSTRY_OPTIONS[0],
  country: "Global",
  service: SERVICE_OPTIONS[0],
  engagement_type: "",

  hero_heading: "",
  hero_subtitle: "",
  hero_summary: "",

  situation_title: "",
  situation_content: "",

  challenge_title: "",
  challenge_content: "",

  solution_title: "",
  solution_content: "",

  impact_title: "",
  impact_content: "",

  result_1: "",
  result_2: "",
  result_3: "",
  result_4: "",

  quote: "",
  quote_author: "",

  image_alt: "",
  
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  canonical_url: "",
  og_title: "",
  og_description: "",

  ai_summary: "",
  target_keywords: "",
  search_intent: "",

  faq_json: "",
  estimated_read_time: "5 min",

  is_published: false,
};

function slugify(value = "") {
  return value
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 90);
}

function validateForPublish(form) {
  const required = [
    ["title", "Title"],
    ["slug", "Slug"],
    ["industry", "Industry"],
    ["service", "Service"],
    ["hero_heading", "Hero Heading"],
    ["challenge_content", "Challenge Content"],
    ["solution_content", "Solution Content"],
    ["impact_content", "Impact Content"],
    ["image_alt", "Image Alt"],
    ["meta_title", "Meta title"],
    ["meta_description", "Meta description"],
  ];

  for (const [key, label] of required) {
    if (!String(form[key] || "").trim()) {
      return `${label} is required before publishing.`;
    }
  }

  if (form.slug !== slugify(form.slug)) {
    return "Slug must be lowercase, URL-safe, and should not contain spaces or special characters.";
  }

  return "";
}

async function compressImageToWebp(file) {
  if (!file) return null;

  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Please upload JPG, PNG, or WEBP image only.");
  }

  const compressedBlob = await imageCompression(file, {
    maxSizeMB: 0.35,
    maxWidthOrHeight: 1400,
    useWebWorker: true,
    fileType: "image/webp",
    initialQuality: 0.82,
  });

  const cleanName = file.name
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .toLowerCase();

  return new File([compressedBlob], `${cleanName || "case-study-image"}.webp`, {
    type: "image/webp",
    lastModified: Date.now(),
  });
}

function buildImagePreviewUrl(imageUrl, supabase) {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;

  return supabase.storage.from("insights").getPublicUrl(imageUrl).data.publicUrl;
}

export default function CaseStudiesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  
  const [removedEditImage, setRemovedEditImage] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  
  const [isDirty, setIsDirty] = useState(false);
  const [manualSlug, setManualSlug] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        (item.title || "").toLowerCase().includes(q) ||
        (item.industry || "").toLowerCase().includes(q) ||
        (item.service || "").toLowerCase().includes(q) ||
        (item.slug || "").toLowerCase().includes(q) ||
        (item.meta_title || "").toLowerCase().includes(q) ||
        (item.country || "").toLowerCase().includes(q) ||
        (item.engagement_type || "").toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const publishedCount = useMemo(
    () => items.filter((item) => item.is_published).length,
    [items]
  );

  async function fetchCaseStudies() {
    setLoading(true);
    setError("");

    const supabase = await getSupabase();

    const { data, error } = await supabase
      .schema("api")
      .from("case_studies")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      setError("Failed to load case studies.");
      setItems([]);
    } else {
      setItems(data || []);
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  function updateFormField(key, value) {
    setIsDirty(true);
    setForm((current) => {
      const next = { ...current, [key]: value };

      if (key === "slug") {
        setManualSlug(true);
      }

      if (key === "title" && !manualSlug) {
        next.slug = slugify(value);
      }

      return next;
    });
  }

  function updateEditField(key, value) {
    setIsDirty(true);
    setEditForm((current) => {
      return { ...current, [key]: value };
    });
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSubmitting(true);

    try {
      let parsedFaq = null;
      if (form.faq_json.trim()) {
        try {
          parsedFaq = JSON.parse(form.faq_json.trim());
        } catch (e) {
          alert("FAQ JSON is invalid.");
          setSubmitting(false);
          return;
        }
      }

      const publishError = form.is_published ? validateForPublish(form) : "";

      if (form.is_published && !imageFile) {
         alert("Featured image is required before publishing.");
         setSubmitting(false);
         return;
      }

      if (publishError) {
        alert(publishError);
        setSubmitting(false);
        return;
      }

      const supabase = await getSupabase();
      let imageUrl = null;

      if (imageFile) {
        const webpFile = await compressImageToWebp(imageFile);
        imageUrl = await uploadCaseStudyImage(webpFile);
      }

      const { error } = await supabase.schema("api").from("case_studies").insert({
        title: form.title.trim(),
        slug: form.slug.trim(),
        industry: form.industry,
        country: form.country,
        service: form.service,
        engagement_type: form.engagement_type.trim(),
        hero_heading: form.hero_heading.trim(),
        hero_subtitle: form.hero_subtitle.trim(),
        hero_summary: form.hero_summary.trim(),
        situation_title: form.situation_title.trim(),
        situation_content: form.situation_content.trim(),
        challenge_title: form.challenge_title.trim(),
        challenge_content: form.challenge_content.trim(),
        solution_title: form.solution_title.trim(),
        solution_content: form.solution_content.trim(),
        impact_title: form.impact_title.trim(),
        impact_content: form.impact_content.trim(),
        result_1: form.result_1.trim(),
        result_2: form.result_2.trim(),
        result_3: form.result_3.trim(),
        result_4: form.result_4.trim(),
        quote: form.quote.trim(),
        quote_author: form.quote_author.trim(),
        featured_image_url: imageUrl,
        image_alt: form.image_alt.trim(),
        meta_title: form.meta_title.trim(),
        meta_description: form.meta_description.trim(),
        meta_keywords: form.meta_keywords ? form.meta_keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
        canonical_url: form.canonical_url.trim(),
        og_title: form.og_title.trim(),
        og_description: form.og_description.trim(),
        ai_summary: form.ai_summary.trim(),
        target_keywords: form.target_keywords ? form.target_keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
        search_intent: form.search_intent.trim(),
        faq_json: parsedFaq,
        estimated_read_time: parseInt(form.estimated_read_time, 10) || 5,
        is_published: Boolean(form.is_published),
      });

      if (error) throw error;

      setForm(EMPTY_FORM);
      setImageFile(null);
      setImagePreview(null);
      setIsDirty(false);
      setManualSlug(false);
      await fetchCaseStudies();
      alert("Case Study added successfully.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to add case study.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdate() {
    if (!editing) return;
    setSubmitting(true);

    try {
      let parsedFaq = null;
      if (editForm.faq_json.trim()) {
        try {
          parsedFaq = JSON.parse(editForm.faq_json.trim());
        } catch (e) {
          alert("FAQ JSON is invalid.");
          setSubmitting(false);
          return;
        }
      }

      const publishError = editForm.is_published
        ? validateForPublish(editForm)
        : "";

      if (editForm.is_published && !editImageFile && removedEditImage && !editing.featured_image_url) {
        alert("Featured image is required before publishing.");
        setSubmitting(false);
        return;
      }

      if (publishError) {
        alert(publishError);
        setSubmitting(false);
        return;
      }

      const supabase = await getSupabase();

      let imageUrl = editing.featured_image_url;
      if (removedEditImage) {
         imageUrl = null;
      }
      
      if (editImageFile) {
        const webpFile = await compressImageToWebp(editImageFile);
        imageUrl = await uploadCaseStudyImage(webpFile);
      }

      const { error } = await supabase
        .schema("api")
        .from("case_studies")
        .update({
          title: editForm.title.trim(),
          slug: editForm.slug.trim(),
          industry: editForm.industry,
          country: editForm.country,
          service: editForm.service,
          engagement_type: editForm.engagement_type.trim(),
          hero_heading: editForm.hero_heading.trim(),
          hero_subtitle: editForm.hero_subtitle.trim(),
          hero_summary: editForm.hero_summary.trim(),
          situation_title: editForm.situation_title.trim(),
          situation_content: editForm.situation_content.trim(),
          challenge_title: editForm.challenge_title.trim(),
          challenge_content: editForm.challenge_content.trim(),
          solution_title: editForm.solution_title.trim(),
          solution_content: editForm.solution_content.trim(),
          impact_title: editForm.impact_title.trim(),
          impact_content: editForm.impact_content.trim(),
          result_1: editForm.result_1.trim(),
          result_2: editForm.result_2.trim(),
          result_3: editForm.result_3.trim(),
          result_4: editForm.result_4.trim(),
          quote: editForm.quote.trim(),
          quote_author: editForm.quote_author.trim(),
          featured_image_url: imageUrl,
          image_alt: editForm.image_alt.trim(),
          meta_title: editForm.meta_title.trim(),
          meta_description: editForm.meta_description.trim(),
          meta_keywords: editForm.meta_keywords ? editForm.meta_keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
          canonical_url: editForm.canonical_url.trim(),
          og_title: editForm.og_title.trim(),
          og_description: editForm.og_description.trim(),
          ai_summary: editForm.ai_summary.trim(),
          target_keywords: editForm.target_keywords ? editForm.target_keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
          search_intent: editForm.search_intent.trim(),
          faq_json: parsedFaq,
          estimated_read_time: parseInt(editForm.estimated_read_time, 10) || 5,
          is_published: Boolean(editForm.is_published),
          updated_at: new Date().toISOString(),
        })
        .eq("id", editing.id);

      if (error) throw error;

      setEditing(null);
      setEditImageFile(null);
      setEditImagePreview(null);
      setRemovedEditImage(false);
      setIsDirty(false);
      await fetchCaseStudies();
      alert("Case Study updated successfully.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to update case study.");
    } finally {
      setSubmitting(false);
    }
  }

  async function togglePublish(item) {
    if (!item.is_published) {
      const publishError = validateForPublish(item);
      if (publishError || !item.featured_image_url) {
        alert(`Cannot publish yet: ${publishError || "Featured image is required"}`);
        return;
      }
    }

    const supabase = await getSupabase();

    const { error } = await supabase
      .schema("api")
      .from("case_studies")
      .update({
        is_published: !item.is_published,
        updated_at: new Date().toISOString(),
      })
      .eq("id", item.id);

    if (error) {
      console.error(error);
      alert("Failed to update publish status.");
      return;
    }

    await fetchCaseStudies();
  }

  async function deleteCaseStudy(id) {
    if (!confirm("This action cannot be undone. Are you sure?")) return;

    const supabase = await getSupabase();

    const { error } = await supabase
      .schema("api")
      .from("case_studies")
      .delete()
      .eq("id", id);

    if (error) {
      console.error(error);
      alert("Failed to delete case study.");
      return;
    }

    await fetchCaseStudies();
  }

  async function startEditing(item) {
    const supabase = await getSupabase();
    setEditing(item);
    setEditForm({
      ...EMPTY_FORM,
      ...item,
      meta_keywords: Array.isArray(item.meta_keywords) ? item.meta_keywords.join(", ") : (item.meta_keywords || ""),
      target_keywords: Array.isArray(item.target_keywords) ? item.target_keywords.join(", ") : (item.target_keywords || ""),
      faq_json: item.faq_json ? JSON.stringify(item.faq_json, null, 2) : "",
      estimated_read_time: item.estimated_read_time ? `${item.estimated_read_time} min` : "5 min",
    });
    setEditImageFile(null);
    setRemovedEditImage(false);
    setIsDirty(false);
    setManualSlug(true); 
    setEditImagePreview(buildImagePreviewUrl(item.featured_image_url, supabase));
  }

  function handleCancelEdit() {
    if (isDirty) {
      if (!confirm("You have unsaved changes. Are you sure you want to leave?")) {
        return;
      }
    }
    setEditing(null);
    setEditImageFile(null);
    setEditImagePreview(null);
    setRemovedEditImage(false);
    setIsDirty(false);
  }

  function handleCreateImageChange(file) {
    setIsDirty(true);
    setImageFile(file || null);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  }

  function handleEditImageChange(file) {
    setIsDirty(true);
    setEditImageFile(file || null);
    if (file) {
      setEditImagePreview(URL.createObjectURL(file));
      setRemovedEditImage(false);
    } else if (editing?.featured_image_url && !removedEditImage) {
      getSupabase().then((supabase) => {
        setEditImagePreview(buildImagePreviewUrl(editing.featured_image_url, supabase));
      });
    } else {
      setEditImagePreview(null);
    }
  }
  
  function removeEditImage() {
    setIsDirty(true);
    setEditImageFile(null);
    setEditImagePreview(null);
    setRemovedEditImage(true);
  }

  return (
    <div className="space-y-12">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Case Studies</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage case studies, success stories, and SEO metadata.
        </p>

        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
            Total: {items.length}
          </span>
          <span className="rounded-full bg-green-100 px-3 py-1 text-green-700">
            Published: {publishedCount}
          </span>
          <span className="rounded-full bg-yellow-100 px-3 py-1 text-yellow-700">
            Drafts: {items.length - publishedCount}
          </span>
        </div>
      </div>

      <div className="rounded-2xl border border-white/20 bg-white/70 p-6 shadow-xl backdrop-blur-xl dark:bg-white/5">
        <h2 className="mb-4 text-lg font-semibold">Add New Case Study</h2>

        <form onSubmit={handleCreate} className="grid gap-5">
          <CaseStudyFormFields
            form={form}
            setField={updateFormField}
            imagePreview={imagePreview}
            onImageChange={handleCreateImageChange}
            onRemoveImage={() => {
              setIsDirty(true);
              setImageFile(null);
              setImagePreview(null);
            }}
          />

          <button
            disabled={submitting}
            className="self-start rounded-xl bg-[rgb(var(--brand-primary))] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:shadow-lg disabled:opacity-60"
          >
            {submitting ? "Adding…" : "Add Case Study"}
          </button>
        </form>
      </div>

      <div className="rounded-2xl border border-white/20 bg-white/70 p-6 shadow-xl backdrop-blur-xl dark:bg-white/5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">Manage Case Studies</h2>
          <input
            type="text"
            placeholder="Search by title, industry, service, or SEO title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-sm rounded-lg border px-4 py-2 text-sm focus:border-[rgb(var(--brand-primary))] focus:outline-none"
          />
        </div>
        
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        {loading ? (
          <div className="mt-6 grid gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
            ))}
          </div>
        ) : (
          <div className="mt-6 grid gap-4">
            {filteredItems.map((u) => (
              <div
                key={u.id}
                className="rounded-2xl border border-white/20 bg-white/70 p-6 shadow-lg backdrop-blur-xl dark:bg-white/5"
              >
                <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-start">
                  <div className="max-w-4xl">
                    <div className="mb-2 flex flex-wrap gap-2">
                      {u.industry && (
                        <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                          {u.industry}
                        </span>
                      )}

                      {u.service && (
                        <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">
                          {u.service}
                        </span>
                      )}

                      {u.slug ? (
                        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                          Slug OK
                        </span>
                      ) : (
                        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                          Missing slug
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold">{u.title}</h3>

                    <p className="mt-2 line-clamp-2 text-sm text-slate-600">
                      {u.hero_summary || u.situation_content}
                    </p>

                    {u.slug && (
                      <p className="mt-2 text-xs text-slate-500">
                        /case-studies/{u.slug}
                      </p>
                    )}
                  </div>

                  <span
                    className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                      u.is_published
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-200 text-slate-700"
                    }`}
                  >
                    {u.is_published ? "Published" : "Draft"}
                  </span>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    onClick={() => startEditing(u)}
                    className="rounded-lg bg-slate-600 px-4 py-2 text-sm text-white hover:bg-slate-700"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => togglePublish(u)}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold ${
                      u.is_published
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-slate-400 text-white hover:bg-slate-500"
                    }`}
                  >
                    {u.is_published ? "Unpublish" : "Publish"}
                  </button>

                  <button
                    onClick={() => deleteCaseStudy(u.id)}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-lg font-semibold">Edit Case Study</h2>

            <div className="grid gap-5">
              <CaseStudyFormFields
                form={editForm}
                setField={updateEditField}
                imagePreview={editImagePreview}
                onImageChange={handleEditImageChange}
                onRemoveImage={removeEditImage}
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCancelEdit}
                className="rounded border px-4 py-2 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={handleUpdate}
                disabled={submitting}
                className="rounded bg-[rgb(var(--brand-primary))] px-4 py-2 text-sm text-white disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CaseStudyFormFields({ form, setField, imagePreview, onImageChange, onRemoveImage }) {
  return (
    <>
      <div className="mb-2 border-b pb-2 text-sm font-bold text-slate-700">Basic Information</div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Title *">
          <input
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            required
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="Case Study Title"
          />
        </Field>

        <Field label="Slug *">
          <input
            value={form.slug}
            onChange={(e) => setField("slug", slugify(e.target.value))}
            required
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="seo-friendly-url"
          />
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Field label="Industry *">
          <select
            value={form.industry}
            onChange={(e) => setField("industry", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {INDUSTRY_OPTIONS.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>

        <Field label="Country">
          <select
            value={form.country}
            onChange={(e) => setField("country", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {COUNTRY_OPTIONS.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>

        <Field label="Service *">
          <select
            value={form.service}
            onChange={(e) => setField("service", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {SERVICE_OPTIONS.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>

        <Field label="Engagement Type">
          <input
            value={form.engagement_type}
            onChange={(e) => setField("engagement_type", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="e.g., Retainer, Project"
          />
        </Field>
      </div>
      
      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Hero Section</div>
      <Field label="Hero Heading *">
        <input
          value={form.hero_heading}
          onChange={(e) => setField("hero_heading", e.target.value)}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Main heading on the page"
        />
      </Field>
      <Field label="Hero Subtitle">
        <input
          value={form.hero_subtitle}
          onChange={(e) => setField("hero_subtitle", e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Subheading text"
        />
      </Field>
      <Field label="Hero Summary">
        <textarea
          value={form.hero_summary}
          onChange={(e) => setField("hero_summary", e.target.value)}
          rows={3}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Short descriptive summary"
        />
      </Field>

      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Situation Section</div>
      <Field label="Situation Title">
        <input
          value={form.situation_title}
          onChange={(e) => setField("situation_title", e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="e.g. The Current Landscape"
        />
      </Field>
      <Field label="Situation Content">
        <textarea
          value={form.situation_content}
          onChange={(e) => setField("situation_content", e.target.value)}
          rows={4}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Describe the initial situation"
        />
      </Field>

      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Challenge Section</div>
      <Field label="Challenge Title">
        <input
          value={form.challenge_title}
          onChange={(e) => setField("challenge_title", e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="e.g. The Core Problem"
        />
      </Field>
      <Field label="Challenge Content *">
        <textarea
          value={form.challenge_content}
          onChange={(e) => setField("challenge_content", e.target.value)}
          rows={4}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Describe the client's challenge"
        />
      </Field>

      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Solution Section</div>
      <Field label="Solution Title">
        <input
          value={form.solution_title}
          onChange={(e) => setField("solution_title", e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="e.g. Our Approach"
        />
      </Field>
      <Field label="Solution Content *">
        <textarea
          value={form.solution_content}
          onChange={(e) => setField("solution_content", e.target.value)}
          rows={4}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Describe the solution provided"
        />
      </Field>

      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Impact Section</div>
      <Field label="Impact Title">
        <input
          value={form.impact_title}
          onChange={(e) => setField("impact_title", e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="e.g. The Difference Made"
        />
      </Field>
      <Field label="Impact Content *">
        <textarea
          value={form.impact_content}
          onChange={(e) => setField("impact_content", e.target.value)}
          rows={4}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Describe the impact of the solution"
        />
      </Field>
      
      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Results</div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Result 1">
          <input
            value={form.result_1}
            onChange={(e) => setField("result_1", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="e.g. 40% Increase in ROI"
          />
        </Field>
        <Field label="Result 2">
          <input
            value={form.result_2}
            onChange={(e) => setField("result_2", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Result 3">
          <input
            value={form.result_3}
            onChange={(e) => setField("result_3", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Result 4">
          <input
            value={form.result_4}
            onChange={(e) => setField("result_4", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </Field>
      </div>

      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Quote Section</div>
      <Field label="Quote Text">
        <textarea
          value={form.quote}
          onChange={(e) => setField("quote", e.target.value)}
          rows={2}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Client testimonial quote"
        />
      </Field>
      <Field label="Quote Author">
        <input
          value={form.quote_author}
          onChange={(e) => setField("quote_author", e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="John Doe, CEO"
        />
      </Field>

      <div className="mb-2 mt-4 border-b pb-2 text-sm font-bold text-slate-700">Featured Image</div>
      <Field label="Image File *">
        {imagePreview ? (
          <div className="mb-3 overflow-hidden rounded-xl border">
            <img
              src={imagePreview}
              alt="Preview"
              loading="lazy"
              className="h-48 w-full object-cover"
            />
            <div className="flex gap-2 p-2">
              <label className="cursor-pointer rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200">
                Replace Image
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/webp"
                  className="hidden"
                  onChange={(e) => onImageChange(e.target.files[0])}
                />
              </label>
              <button
                type="button"
                onClick={onRemoveImage}
                className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200"
              >
                Remove Image
              </button>
            </div>
          </div>
        ) : (
          <label className="flex w-full cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 py-10 hover:border-[rgb(var(--brand-primary))] hover:bg-slate-100">
            <span className="text-sm font-medium text-slate-500">
              Click to upload a high-res WebP/JPG/PNG
            </span>
            <input
              type="file"
              accept="image/png, image/jpeg, image/webp"
              className="hidden"
              onChange={(e) => onImageChange(e.target.files[0])}
            />
          </label>
        )}
      </Field>

      <Field label="Image Alt *">
        <input
          value={form.image_alt}
          onChange={(e) => setField("image_alt", e.target.value)}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Describe the image for accessibility"
        />
      </Field>

      <div className="my-4 border-t pt-4">
        <h3 className="mb-4 text-sm font-bold text-slate-700">SEO Configuration</h3>
        
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Meta Title *">
            <input
              value={form.meta_title}
              onChange={(e) => setField("meta_title", e.target.value)}
              required
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Recommended: 50–60 characters"
            />
          </Field>

          <Field label="Meta Keywords">
            <input
              value={form.meta_keywords}
              onChange={(e) => setField("meta_keywords", e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="keyword one, keyword two"
            />
          </Field>
        </div>

        <Field label="Meta Description *">
          <textarea
            value={form.meta_description}
            onChange={(e) => setField("meta_description", e.target.value)}
            rows={3}
            required
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="Recommended: 140–160 characters"
          />
        </Field>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Field label="OG Title">
            <input
              value={form.og_title}
              onChange={(e) => setField("og_title", e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Open Graph Title (Optional)"
            />
          </Field>
          
          <Field label="Canonical URL">
            <input
              value={form.canonical_url}
              onChange={(e) => setField("canonical_url", e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Canonical URL (Optional)"
            />
          </Field>
        </div>
        
        <div className="mt-4">
          <Field label="OG Description">
            <textarea
              value={form.og_description}
              onChange={(e) => setField("og_description", e.target.value)}
              rows={2}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Open Graph Description (Optional)"
            />
          </Field>
        </div>
      </div>

      <div className="my-4 border-t pt-4">
        <h3 className="mb-4 text-sm font-bold text-slate-700">AI Search & Discovery</h3>
        
        <Field label="AI Summary">
          <textarea
            value={form.ai_summary}
            onChange={(e) => setField("ai_summary", e.target.value)}
            rows={2}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="Machine-readable summary for AI search engines"
          />
        </Field>
        
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Field label="Target Keywords">
            <input
              value={form.target_keywords}
              onChange={(e) => setField("target_keywords", e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Comma separated"
            />
          </Field>

          <Field label="Search Intent">
            <input
              value={form.search_intent}
              onChange={(e) => setField("search_intent", e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="e.g. Informational, Navigational, Transactional"
            />
          </Field>
        </div>
      </div>
      
      <div className="my-4 border-t pt-4">
        <h3 className="mb-4 text-sm font-bold text-slate-700">Additional Meta</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Estimated Read Time">
            <input
              value={form.estimated_read_time}
              onChange={(e) => setField("estimated_read_time", e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="e.g. 5 min"
            />
          </Field>
        </div>
        
        <div className="mt-4">
          <Field label="FAQ JSON">
            <textarea
              value={form.faq_json}
              onChange={(e) => setField("faq_json", e.target.value)}
              rows={4}
              className="w-full rounded-lg border px-3 py-2 text-sm font-mono text-xs"
              placeholder="[ { &quot;question&quot;: &quot;...&quot;, &quot;answer&quot;: &quot;...&quot; } ]"
            />
          </Field>
        </div>
      </div>

      <Field label="Publish Status">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => setField("is_published", e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[rgb(var(--brand-primary))]"
          />
          Published
        </label>
      </Field>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      {children}
    </div>
  );
}
